/**
 * Add AI credit… (TMMPDM-0088, TMMAPI-0152): credits a person's AI wallet with a reason. The
 * idempotency key is made when the dialog opens and reused for every submit in that opening, so a
 * retried submit never credits twice; a reopen makes a new key. The credit is in the person's
 * wallet currency (0153 item 32): the amount carries that currency when the row has a wallet, and
 * says "their subscription's currency" otherwise. Every failure keeps the dialog open (TMM-B4).
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { AdminAiCreditResponse } from '@/api/tmm/types';
import { SubmitButton } from '@/components/page';
import { useCopy } from '@/hooks/useCopy';
import { formatMoney, majorToMinor, minorUnitDigits } from '@/format';

/** 10,000,000.00 in major units (the server's 1,000,000,000 minor). */
const MAX_MAJOR = 10_000_000;
const REASON_MAX = 255;

export interface AiCreditDialogProps {
  open: boolean;
  slug: string;
  userId: number;
  person: string;
  /** The person's wallet currency; absent when the row has no wallet. */
  walletCurrency: string | undefined;
  onClose: () => void;
  onCredited: (result: AdminAiCreditResponse) => void;
}

export function AiCreditDialog({ open, ...rest }: AiCreditDialogProps) {
  const { t } = useCopy();
  const titleId = 'ai-credit-dialog-title';
  return (
    <Dialog open={open} onClose={rest.onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      <DialogTitle id={titleId}>
        {t('aiUsage.credit.title', 'Add AI credit for {{person}}', { person: rest.person })}
      </DialogTitle>
      {open ? <AiCreditForm {...rest} /> : null}
    </Dialog>
  );
}

function validAmount(value: string, currency: string | undefined): boolean {
  const trimmed = value.trim();
  const digits = currency ? minorUnitDigits(currency) : 2;
  const pattern = digits > 0 ? new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`) : /^\d+$/;
  if (!pattern.test(trimmed)) return false;
  const n = Number(trimmed);
  return n > 0 && n <= MAX_MAJOR;
}

function AiCreditForm({
  slug,
  userId,
  person,
  walletCurrency,
  onClose,
  onCredited,
}: Omit<AiCreditDialogProps, 'open'>) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  // One key per opening: this form mounts when the dialog opens.
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const amountMinor = majorToMinor(amount, walletCurrency ?? '');
  const amountText =
    validAmount(amount, walletCurrency) && walletCurrency
      ? formatMoney(amountMinor, walletCurrency, locale)
      : amount.trim();

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!validAmount(amount, walletCurrency)) {
      next.amountMinor = t(
        'aiUsage.credit.amountRule',
        'Enter an amount above zero and at most 10,000,000.00.',
      );
    }
    const trimmed = reason.trim();
    if (trimmed === '') {
      next.reason = t('aiUsage.credit.reasonRequired', 'Reason is required.');
    } else if (trimmed.length > REASON_MAX) {
      next.reason = t('aiUsage.credit.reasonTooLong', 'Use at most 255 characters.');
    }
    return next;
  };

  const submit = async () => {
    if (inFlight.current) return;
    const next = validate();
    setFieldErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const result = await adminApi.addAiCredit(slug, userId, {
        amountMinor,
        reason: reason.trim(),
        idempotencyKey,
      });
      onCredited(result);
    } catch (err) {
      if (err instanceof ApiError && String(err.code) === 'SUBSCRIPTION_REQUIRED') {
        setFormError(
          t(
            'aiUsage.credit.noSubscription',
            "This person has no live subscription, so their AI wallet can't be credited.",
          ),
        );
      } else if (err instanceof ApiError && String(err.code) === 'IDEMPOTENCY_CONFLICT') {
        setFormError(
          t(
            'aiUsage.credit.keyConflict',
            'This credit was already sent with a different amount. Close this and start again.',
          ),
        );
      } else if (isValidationFailed(err)) {
        const fields = validationFields(err, t);
        setFieldErrors(fields);
        if (!fields.amountMinor && !fields.reason) {
          setFormError(errorCopy(err, t, t('aiUsage.credit.error', "Couldn't add the AI credit.")));
        }
      } else {
        setFormError(errorCopy(err, t, t('aiUsage.credit.error', "Couldn't add the AI credit.")));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const consequence = walletCurrency
    ? t(
        'aiUsage.credit.consequence',
        "{{amount}} is added to {{person}}'s AI wallet. It's recorded with your reason.",
        { amount: amountText === '' ? '—' : amountText, person },
      )
    : t(
        'aiUsage.credit.consequenceNoWallet',
        "{{amount}} in their subscription's currency is added to {{person}}'s AI wallet. It's recorded with your reason.",
        { amount: amountText === '' ? '—' : amountText, person },
      );

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            id="ai-credit-amount"
            label={t('aiUsage.credit.amount', 'Amount')}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setFieldErrors(({ amountMinor: _dropped, ...rest }) => rest);
            }}
            error={Boolean(fieldErrors.amountMinor)}
            helperText={fieldErrors.amountMinor}
            required
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              ...(walletCurrency
                ? {
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">{walletCurrency}</InputAdornment>
                      ),
                    },
                  }
                : {}),
            }}
          />
          <TextField
            id="ai-credit-reason"
            label={t('aiUsage.credit.reason', 'Reason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setFieldErrors(({ reason: _dropped, ...rest }) => rest);
            }}
            error={Boolean(fieldErrors.reason)}
            helperText={fieldErrors.reason}
            required
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
          />
          <DialogContentText>{consequence}</DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('aiUsage.credit.confirm', 'Add credit')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
